/**
 * OpenAI Realtime voice adapter (P2-08).
 *
 * Talks to `wss://api.openai.com/v1/realtime` directly from the browser,
 * authenticated with a short-lived ephemeral `clientSecret` minted by the
 * cloneai API (see `POST /v1/public/agents/.../session`). The secret lives
 * only in memory on this instance — it is never logged, persisted, or posted
 * to other windows.
 *
 * Audio path:
 *   mic → AudioWorkletNode('pcm16-recorder') → Int16Array via transferable
 *       → base64 → ws.send({ type: 'input_audio_buffer.append', audio })
 *
 *   ws.onmessage('response.output_audio.delta')
 *       → base64 → Int16 → Float32 → AudioBuffer → scheduled playback
 *
 * The microphone remains live during normal response playback. Server VAD
 * reports a new speech start, at which point the current response and locally
 * queued audio are cancelled/truncated before the new turn is handled. Agents
 * can separately protect their opening greeting from speaker echo and ambient
 * noise by gating microphone frames until its queued playback is complete.
 */

import type {
  AgentKnowledgeSearchResponse,
  AgentKnowledgeSearchResult,
  AgentSessionResponse,
  TranscriptTurnInput,
} from '@cloneai/shared-types/api';
import type { OpenAIVoice } from '@cloneai/shared-types';

import { base64ToPcm16, pcm16ToBase64, pcm16ToFloat32 } from './pcm16.js';
import type { SessionRecorder } from './sessionRecorder.js';
import type { VoiceSession } from './VoiceSession.js';

type OpenAIRealtimeDescriptor = Extract<AgentSessionResponse, { kind: 'openai-realtime' }>;

/**
 * Minimal shape we rely on from incoming Realtime events. The full schema
 * has many more fields, but optional-chaining + safe extraction keeps this
 * resilient to additions on OpenAI's side.
 */
interface RealtimeInboundEvent {
  type?: string;
  event_id?: string;
  response_id?: string;
  item_id?: string;
  content_index?: number;
  error?: { message?: string; code?: string } | undefined;
  delta?: string;
  transcript?: string;
  text?: string;
  usage?: RealtimeUsage;
  call_id?: string;
  name?: string;
  arguments?: string;
  response?: {
    id?: string;
    status?: string;
    status_details?: {
      type?: string;
      reason?: string;
      error?: { code?: string; message?: string };
    };
    output?: RealtimeOutputItem[];
    usage?: RealtimeUsage;
  };
}

interface RealtimeOutputItem {
  type?: string;
  name?: string;
  call_id?: string;
  arguments?: string;
}

interface RealtimeFunctionCall {
  readonly name: string;
  readonly callId: string;
  readonly argumentsJson: string;
  readonly usage?: RealtimeUsage;
  readonly latencyMs?: number;
}

export interface RealtimeUsage {
  type?: string;
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  input_token_details?: {
    cached_tokens?: number;
    text_tokens?: number;
    audio_tokens?: number;
    cached_tokens_details?: {
      text_tokens?: number;
      audio_tokens?: number;
    };
  };
  output_token_details?: {
    text_tokens?: number;
    audio_tokens?: number;
  };
}

export const KNOWLEDGE_SEARCH_TOOL_NAME = 'search_agent_knowledge';
export const REALTIME_MAX_OUTPUT_TOKENS = 512;
export const REALTIME_RESPONSE_TIMEOUT_MS = 20_000;
const REALTIME_PLATFORM_MAX_OUTPUT_TOKENS = 1024;
const REALTIME_PLATFORM_MAX_RESPONSE_TIMEOUT_MS = 45_000;
const REALTIME_REPETITION_RECOVERY_MAX_OUTPUT_TOKENS = 256;
export const REALTIME_PREPARATION_TIMEOUT_MS = 20_000;

const LANGUAGE_NAMES: Readonly<Record<string, string>> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
};

interface PlaybackContext {
  readonly responseId: string;
  readonly itemId: string;
  readonly contentIndex: number;
  readonly startedAt: number;
  readonly scheduledUntil: number;
}

interface KnowledgeSourceSummary {
  readonly sourceId: string;
  readonly chunkId: string;
  readonly title: string;
  readonly score: number;
}

export interface OpenAIRealtimeKnowledgeTool {
  readonly search: (query: string) => Promise<AgentKnowledgeSearchResponse>;
}

interface OpenAIRealtimeSessionOptions {
  readonly knowledgeTool?: OpenAIRealtimeKnowledgeTool;
  /** Fires once, when the first nonempty audio chunk is scheduled for playback. */
  readonly onFirstAudio?: () => void;
}

function tokenCount(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
  return Math.trunc(value);
}

export function normalizeRealtimeUsage(
  usage: RealtimeUsage | undefined,
): NonNullable<TranscriptTurnInput['usage']> | undefined {
  if (!usage) return undefined;

  const inputTokens = tokenCount(usage.input_tokens);
  const outputTokens = tokenCount(usage.output_tokens);
  const totalTokens = tokenCount(usage.total_tokens);
  const inputDetails = usage.input_token_details;
  const outputDetails = usage.output_token_details;

  if (
    !inputDetails &&
    !outputDetails &&
    inputTokens === 0 &&
    outputTokens === 0 &&
    totalTokens === 0
  ) {
    return undefined;
  }

  const cachedText = tokenCount(
    inputDetails?.cached_tokens_details?.text_tokens ?? inputDetails?.cached_tokens,
  );
  const cachedAudio = tokenCount(inputDetails?.cached_tokens_details?.audio_tokens);

  return {
    inputTokens,
    inputTokensCached: tokenCount(inputDetails?.cached_tokens) || cachedText + cachedAudio,
    inputTokensCachedText: cachedText,
    inputTokensCachedAudio: cachedAudio,
    inputTokensText: tokenCount(inputDetails?.text_tokens ?? (inputDetails ? 0 : inputTokens)),
    inputTokensAudio: tokenCount(inputDetails?.audio_tokens),
    outputTokens,
    outputTokensText: tokenCount(outputDetails?.text_tokens ?? (outputDetails ? 0 : outputTokens)),
    outputTokensAudio: tokenCount(outputDetails?.audio_tokens),
    totalTokens,
  };
}

export function buildKnowledgeSearchToolDefinition(): unknown {
  return {
    type: 'function',
    name: KNOWLEDGE_SEARCH_TOOL_NAME,
    description:
      "Search this agent's approved knowledge base. This lookup is required before answering factual product, model, brand, price, availability, specification, recommendation, service, policy, or troubleshooting questions.",
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'The concise factual question or keywords to search for.',
        },
      },
      required: ['query'],
      additionalProperties: false,
    },
  };
}

/** Generate the user-facing answer after a required knowledge lookup. */
export function buildToolFreeResponseCreatePayload(
  maxOutputTokens = REALTIME_MAX_OUTPUT_TOKENS,
): unknown {
  return {
    type: 'response.create',
    response: {
      tool_choice: 'none',
      max_output_tokens: Math.min(maxOutputTokens, REALTIME_PLATFORM_MAX_OUTPUT_TOKENS),
    },
  };
}

/** Start a response for one validated user turn, inheriting the session tool policy. */
export function buildUserTurnResponseCreatePayload(
  maxOutputTokens = REALTIME_MAX_OUTPUT_TOKENS,
): unknown {
  return {
    type: 'response.create',
    response: {
      max_output_tokens: Math.min(maxOutputTokens, REALTIME_PLATFORM_MAX_OUTPUT_TOKENS),
    },
  };
}

/**
 * Recover from a cancelled runaway response without re-entering a tool loop or
 * making another factual claim. The model gets one short clarification turn;
 * a second runaway response is cancelled without recursively recovering.
 */
export function buildRepetitionRecoveryResponseCreatePayload(
  maxOutputTokens = REALTIME_MAX_OUTPUT_TOKENS,
): unknown {
  return {
    type: 'response.create',
    response: {
      instructions:
        "Recover from the stopped repetitive response. In the session's active language, ask exactly one short and direct clarification question about what the user wants next. If the user gave an ambiguous confirmation after alternatives, name those alternatives in the question. Do not make factual claims, use introductory filler, mention the stopped response, or say that you will clarify or explain.",
      tool_choice: 'none',
      max_output_tokens: Math.min(maxOutputTokens, REALTIME_REPETITION_RECOVERY_MAX_OUTPUT_TOKENS),
    },
  };
}

export function buildResponseCancelPayload(responseId: string): unknown {
  return { type: 'response.cancel', response_id: responseId };
}

export function buildConversationItemDeletePayload(itemId: string): unknown {
  return { type: 'conversation.item.delete', item_id: itemId };
}

export function buildConversationItemTruncatePayload(args: {
  readonly itemId: string;
  readonly contentIndex: number;
  readonly audioEndMs: number;
}): unknown {
  return {
    type: 'conversation.item.truncate',
    item_id: args.itemId,
    content_index: args.contentIndex,
    audio_end_ms: Math.max(0, Math.round(args.audioEndMs)),
  };
}

/** Ignore punctuation/noise and tiny trailing fragments rather than answering them. */
export function isActionableTranscript(value: string): boolean {
  const text = value.trim();
  if (text.length === 0) return false;
  const letterGroups = text.match(/\p{L}+/gu) ?? [];
  const letterCount = letterGroups.join('').length;
  if (letterCount < 2) return false;
  return !(/[.…]$/.test(text) && letterCount < 5);
}

function normalizedResponseWords(value: string): string[] {
  return (
    value
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .match(/[\p{L}\p{N}]+/gu) ?? []
  );
}

/**
 * Detects pathological repetition in a single streaming assistant response.
 * Exact four-word loops catch general degeneration. The transition counter
 * catches semantic paraphrase loops such as repeated variants of "let me
 * clarify/explain", which do not share a long exact n-gram.
 */
export function hasRunawayResponseRepetition(value: string): boolean {
  const words = normalizedResponseWords(value);
  if (words.length < 18) return false;

  const ngramCounts = new Map<string, number>();
  for (let index = 0; index <= words.length - 4; index += 1) {
    const ngram = words.slice(index, index + 4).join(' ');
    const count = (ngramCounts.get(ngram) ?? 0) + 1;
    if (count >= 3) return true;
    ngramCounts.set(ngram, count);
  }

  const normalized = words.join(' ');
  const metaTransitionPatterns = [
    /\b(?:laissez moi(?: vous)?|je vais(?: vous)?|je(?: vous)?|un instant(?: je)?) (?:[\p{L}\p{N}]+ ){0,3}(?:clarifi[\p{L}]*|expliqu[\p{L}]*)\b/gu,
    /\b(?:let me|i will|i ll|i am going to) (?:[\p{L}\p{N}]+ ){0,3}(?:clarif[\p{L}]*|explain[\p{L}]*)\b/gu,
    /\b(?:dejame|voy a|permiteme) (?:[\p{L}\p{N}]+ ){0,3}(?:aclar[\p{L}]*|explic[\p{L}]*)\b/gu,
  ];
  const transitionCount = metaTransitionPatterns.reduce(
    (count, pattern) => count + (normalized.match(pattern)?.length ?? 0),
    0,
  );
  return transitionCount >= 3;
}

export function appendActiveLanguageInstruction(
  instructions: string,
  language?: string,
  policy?: OpenAIRealtimeDescriptor['runtimePolicy'],
): string {
  const normalized = language?.trim().toLowerCase();
  if (!normalized) return instructions;
  const displayName = LANGUAGE_NAMES[normalized] ?? normalized;
  const languageSwitchPolicy = policy?.languageSwitchPolicy ?? 'explicit-only';
  const languageSwitchInstruction =
    languageSwitchPolicy === 'locked'
      ? 'Never switch languages during this session.'
      : languageSwitchPolicy === 'automatic'
        ? 'Switch only when the user clearly and consistently uses another language; never switch because of a short, ambiguous, or mis-transcribed utterance.'
        : 'Switch only when the user explicitly asks to change language.';
  const maxSentences = policy?.maxSpokenSentences ?? 4;
  return `${instructions.trim()}\n\nACTIVE LANGUAGE (MANDATORY): Respond entirely in ${displayName} (${normalized}). ${languageSwitchInstruction} Keep each spoken answer concise and complete it in no more than ${maxSentences} short sentences.`;
}

export function buildFunctionCallOutputPayload(callId: string, output: unknown): unknown {
  return {
    type: 'conversation.item.create',
    item: {
      type: 'function_call_output',
      call_id: callId,
      output: typeof output === 'string' ? output : JSON.stringify(output),
    },
  };
}

export function parseKnowledgeToolArguments(argumentsJson: string | undefined): {
  query: string;
} | null {
  if (!argumentsJson) return null;
  try {
    const parsed = JSON.parse(argumentsJson) as { query?: unknown };
    if (typeof parsed.query !== 'string') return null;
    const query = parsed.query.trim();
    return query.length > 0 ? { query } : null;
  } catch {
    return null;
  }
}

function summarizeKnowledgeSources(
  results: readonly AgentKnowledgeSearchResult[],
): KnowledgeSourceSummary[] {
  return results.map((result) => ({
    sourceId: result.sourceId,
    chunkId: result.chunkId,
    title: result.title,
    score: result.score,
  }));
}

export function buildRealtimeSessionUpdatePayload(args: {
  readonly instructions: string;
  readonly voice: OpenAIVoice;
  readonly inputLanguage?: string | undefined;
  readonly knowledgeToolEnabled?: boolean;
  readonly knowledgeLookupPolicy?: 'factual' | 'every-turn';
  readonly maxOutputTokens?: number;
  readonly turnTakingProfile?: 'patient' | 'balanced' | 'responsive';
}): unknown {
  const eagerness =
    args.turnTakingProfile === 'responsive'
      ? 'high'
      : args.turnTakingProfile === 'balanced'
        ? 'auto'
        : 'low';
  const maxOutputTokens = Math.min(
    args.maxOutputTokens ?? REALTIME_MAX_OUTPUT_TOKENS,
    REALTIME_PLATFORM_MAX_OUTPUT_TOKENS,
  );
  return {
    type: 'session.update',
    session: {
      type: 'realtime',
      instructions: args.instructions,
      max_output_tokens: maxOutputTokens,
      ...(args.knowledgeToolEnabled
        ? {
            tools: [buildKnowledgeSearchToolDefinition()],
            // Force one lookup for each validated user-turn response. The
            // follow-up response after the tool result overrides this to
            // `none`, so the model answers instead of entering a tool loop.
            tool_choice:
              args.knowledgeLookupPolicy === 'factual'
                ? 'auto'
                : { type: 'function', name: KNOWLEDGE_SEARCH_TOOL_NAME },
          }
        : {}),
      audio: {
        input: {
          transcription: {
            model: 'whisper-1',
            ...(args.inputLanguage ? { language: args.inputLanguage } : {}),
          },
          turn_detection: {
            type: 'semantic_vad',
            eagerness,
            // Validate the completed transcript client-side before asking
            // for a response, so punctuation/noise cannot trigger the LLM.
            create_response: false,
            interrupt_response: false,
          },
        },
        output: {
          voice: args.voice,
        },
      },
    },
  };
}

export function voiceTelemetryValue(voice: OpenAIVoice): string {
  return typeof voice === 'string' ? voice : voice.id;
}

export class OpenAIRealtimeSession implements VoiceSession {
  public onTranscript?: (text: string) => void;
  public onAgentSpeechStart?: () => void;
  public onAgentSpeechEnd?: () => void;
  public onError?: (err: Error) => void;
  public onSessionEnded?: () => void;

  private ws: WebSocket | null = null;
  private audioCtx: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;

  private nextPlayTime = 0;
  private agentSpeechChunks = 0;
  private playbackSources = new Map<AudioBufferSourceNode, string | undefined>();
  private playbackContexts = new Map<string, PlaybackContext>();
  private sessionConfigured = false;
  private preparationPromise: Promise<void> | null = null;
  private resolvePreparation: (() => void) | null = null;
  private rejectPreparation: ((error: Error) => void) | null = null;
  private preparationTimeout: ReturnType<typeof setTimeout> | null = null;
  private activationPromise: Promise<void> | null = null;
  private rejectAudioResume: ((error: Error) => void) | null = null;
  private audioResumeTimeout: ReturnType<typeof setTimeout> | null = null;
  private firstAudioScheduled = false;
  private greetingSent = false;
  private openingGreetingProtectionActive = false;
  private openingGreetingResponsePending = false;
  private openingGreetingResponseId: string | undefined;
  private openingGreetingResponseDone = false;
  private stopped = false;
  private closeExpected = false;

  // Phase-3 instrumentation. Tracks per-response timings so we can compute
  // `latencyMs` (user-stop → first response token) and emit a TranscriptTurn
  // when `response.done` arrives with usage data.
  private responseStartTs = new Map<string, number>();
  private responseTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private activeResponseIds = new Set<string>();
  private assistantTextByResponse = new Map<string, string>();
  private assistantTranscriptDeltasByResponse = new Map<string, string>();
  private responseOutputItemIds = new Map<string, string>();
  private interruptedResponseIds = new Set<string>();
  private timedOutResponseIds = new Set<string>();
  private repetitionStoppedResponseIds = new Set<string>();
  private repetitionRecoveryGenerations = new Map<string, number>();
  private recoveryResponseIds = new Set<string>();
  private nextResponseIsRecovery = false;
  private inputGeneration = 0;
  private activeInputLanguage: string | undefined;
  private handledFunctionCalls = new Set<string>();
  private pendingFunctionCalls = new Set<string>();
  private supersededFunctionCalls = new Set<string>();

  constructor(
    private readonly descriptor: OpenAIRealtimeDescriptor,
    private readonly recorder?: SessionRecorder,
    inputLanguage?: string,
    private readonly options: OpenAIRealtimeSessionOptions = {},
  ) {
    this.activeInputLanguage = inputLanguage;
  }

  prepare(): Promise<void> {
    if (this.stopped) return Promise.reject(new Error('Voice session has stopped'));
    if (this.preparationPromise) return this.preparationPromise;
    this.preparationPromise = new Promise<void>((resolve, reject) => {
      this.resolvePreparation = resolve;
      this.rejectPreparation = reject;
      this.preparationTimeout = setTimeout(() => {
        this.failPreparation(new Error('Voice connection timed out'));
        this.shutdown(true);
      }, REALTIME_PREPARATION_TIMEOUT_MS);
    });
    void this.openTransport().catch((error: unknown) => {
      this.failPreparation(error instanceof Error ? error : new Error('Voice preparation failed'));
      this.shutdown(true);
    });
    return this.preparationPromise;
  }

  start(): Promise<void> {
    if (this.activationPromise) return this.activationPromise;
    this.activationPromise = (async () => {
      await this.prepare();
      if (this.stopped) throw new Error('Voice session has stopped');
      if (this.audioCtx?.state === 'suspended') await this.resumeAudioContext(this.audioCtx);
      if (this.stopped) throw new Error('Voice session has stopped');
      this.beginConversation();
    })();
    return this.activationPromise;
  }

  private resumeAudioContext(audioCtx: AudioContext): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.rejectAudioResume = reject;
      this.audioResumeTimeout = setTimeout(() => {
        this.failAudioResume(new Error('Audio playback could not start. Try starting again.'));
        this.shutdown(true);
      }, REALTIME_PREPARATION_TIMEOUT_MS);
      void audioCtx.resume().then(
        () => {
          this.clearAudioResume();
          resolve();
        },
        (error: unknown) => {
          this.failAudioResume(error instanceof Error ? error : new Error('Audio playback failed'));
        },
      );
    });
  }

  private clearAudioResume(): void {
    if (this.audioResumeTimeout !== null) clearTimeout(this.audioResumeTimeout);
    this.audioResumeTimeout = null;
    this.rejectAudioResume = null;
  }

  private failAudioResume(error: Error): void {
    this.rejectAudioResume?.(error);
    this.clearAudioResume();
  }

  private async openTransport(): Promise<void> {
    // 1. AudioContext at 24 kHz (matches Realtime API wire format).
    const AudioContextCtor =
      window.AudioContext ??
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      throw new Error('Web Audio API not supported in this browser');
    }
    const audioCtx = new AudioContextCtor({ sampleRate: 24000 });
    this.audioCtx = audioCtx;

    // 2. Register the AudioWorklet processor (served from /public/).
    await audioCtx.audioWorklet.addModule('/worklets/pcm16-recorder.js');
    if (this.stopped) return;

    // 3. Open the Realtime WebSocket. Auth via the subprotocol field —
    //    the browser strips Authorization headers from `new WebSocket(...)`,
    //    so OpenAI uses this "insecure-api-key" subprotocol instead.
    const url =
      `wss://api.openai.com/v1/realtime?model=${encodeURIComponent(this.descriptor.model)}` +
      (typeof this.descriptor.voice === 'string'
        ? `&voice=${encodeURIComponent(this.descriptor.voice)}`
        : '');
    const ws = new WebSocket(url, [
      'realtime',
      `openai-insecure-api-key.${this.descriptor.clientSecret}`,
    ]);
    this.ws = ws;

    ws.onerror = (): void => {
      if (this.closeExpected || this.stopped) return;
      const error = new Error('WebSocket error');
      this.failPreparation(error);
      this.onError?.(error);
      this.recorder?.recordImmediateEvent({
        type: 'error',
        timestamp: new Date().toISOString(),
        payload: { source: 'websocket-error' },
      });
      this.shutdown(false);
    };
    ws.onclose = (event: CloseEvent): void => {
      this.failPreparation(new Error('Voice connection closed before it was ready'));
      // Anything other than 1000 (normal) / 1005 (no status) suggests an
      // abnormal teardown — surface as a connection-drop event so the
      // session report shows what happened.
      const expected = this.closeExpected;
      if (!expected && event.code !== 1000 && event.code !== 1005) {
        this.recorder?.recordImmediateEvent({
          type: 'connection-drop',
          timestamp: new Date().toISOString(),
          payload: { code: event.code, reason: event.reason ?? '' },
        });
      }
      if (!expected) {
        this.shutdown(false);
        this.onSessionEnded?.();
      }
      this.stopped = true;
      this.clearRefs();
    };
    ws.onmessage = (event: MessageEvent<string>): void => {
      this.handleMessage(event);
    };
  }

  stop(): void {
    this.shutdown(true);
  }

  setLanguage(language: string): void {
    const normalized = language.trim().toLowerCase();
    if (!normalized || normalized === this.activeInputLanguage) return;
    this.activeInputLanguage = normalized;
    if (this.sessionConfigured && !this.stopped) this.sendSessionUpdate();
  }

  private shutdown(expectedClose: boolean): void {
    // Idempotent — safe to call more than once.
    if (this.stopped) return;
    this.stopped = true;
    this.closeExpected = expectedClose;
    this.failPreparation(new Error('Voice session has stopped'));
    this.failAudioResume(new Error('Voice session has stopped'));

    const ws = this.ws;
    if (ws && ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }

    if (this.mediaStream) {
      for (const track of this.mediaStream.getTracks()) {
        track.stop();
      }
    }

    try {
      this.workletNode?.disconnect();
    } catch {
      /* ignore */
    }
    try {
      this.sourceNode?.disconnect();
    } catch {
      /* ignore */
    }
    this.stopPlayback();
    for (const timer of this.responseTimeouts.values()) clearTimeout(timer);
    this.responseTimeouts.clear();

    try {
      void this.audioCtx?.close();
    } catch {
      /* ignore */
    }
  }

  // ---------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------

  private clearRefs(): void {
    this.ws = null;
    this.audioCtx = null;
    this.mediaStream = null;
    this.workletNode = null;
    this.sourceNode = null;
  }

  private failPreparation(error: Error): void {
    if (this.preparationTimeout !== null) clearTimeout(this.preparationTimeout);
    this.preparationTimeout = null;
    this.rejectPreparation?.(error);
    this.rejectPreparation = null;
    this.resolvePreparation = null;
  }

  private beginConversation(): void {
    if (this.greetingSent || this.stopped) return;
    this.greetingSent = true;
    this.openingGreetingProtectionActive = this.descriptor.runtimePolicy.protectOpeningGreeting;
    this.openingGreetingResponsePending = this.openingGreetingProtectionActive;
    void this.startMicrophone().catch((err: unknown) => {
      if (this.stopped) return;
      this.onError?.(err instanceof Error ? err : new Error('Microphone error'));
      this.recorder?.recordImmediateEvent({
        type: 'mic-denied',
        timestamp: new Date().toISOString(),
        payload: { code: (err as { name?: string } | null)?.name ?? 'unknown' },
      });
    });
    this.safeSend(
      buildToolFreeResponseCreatePayload(this.descriptor.runtimePolicy.maxOutputTokens),
    );
  }

  private handleMessage(event: MessageEvent<string>): void {
    if (this.stopped) return;
    let msg: RealtimeInboundEvent;
    try {
      msg = JSON.parse(event.data) as RealtimeInboundEvent;
    } catch (err) {
      this.onError?.(err instanceof Error ? err : new Error('Failed to parse Realtime event'));
      return;
    }

    switch (msg.type) {
      case 'session.created': {
        if (this.sessionConfigured) return;
        this.sessionConfigured = true;
        this.sendSessionUpdate();
        return;
      }
      case 'session.updated': {
        if (this.preparationTimeout !== null) clearTimeout(this.preparationTimeout);
        this.preparationTimeout = null;
        this.resolvePreparation?.();
        this.resolvePreparation = null;
        this.rejectPreparation = null;
        return;
      }
      case 'response.created': {
        // Stash the start timestamp keyed on response.id so we can compute
        // `latencyMs` when response.done fires.
        if (msg.response?.id) {
          const responseId = msg.response.id;
          if (this.openingGreetingResponsePending) {
            this.openingGreetingResponsePending = false;
            this.openingGreetingResponseId = responseId;
          }
          this.responseStartTs.set(responseId, Date.now());
          this.activeResponseIds.add(responseId);
          if (this.nextResponseIsRecovery) {
            this.recoveryResponseIds.add(responseId);
            this.nextResponseIsRecovery = false;
          }
          this.startResponseTimeout(responseId);
        }
        return;
      }
      case 'response.done': {
        // Token usage + latency. Build a TranscriptTurn for the assistant
        // turn and post it via the recorder. The text is the transcript we
        // accumulated from `response.output_audio_transcript.done`.
        const responseId = msg.response?.id;
        const startTs = responseId ? this.responseStartTs.get(responseId) : undefined;
        const latencyMs = startTs ? Date.now() - startTs : undefined;
        if (responseId) {
          this.clearResponseTimeout(responseId);
          this.responseStartTs.delete(responseId);
          this.activeResponseIds.delete(responseId);
          if (responseId === this.openingGreetingResponseId) {
            this.openingGreetingResponseDone = true;
            this.maybeReleaseOpeningGreetingProtection();
          }
        }

        const status = msg.response?.status;
        const responseCompleted = status === undefined || status === 'completed';

        const functionCalls = (responseCompleted ? this.extractFunctionCalls(msg) : []).map(
          (call) => ({
            ...call,
            ...(msg.response?.usage ? { usage: msg.response.usage } : {}),
            ...(latencyMs !== undefined ? { latencyMs } : {}),
          }),
        );
        if (functionCalls.length > 0) {
          for (const call of functionCalls) {
            void this.handleFunctionCall(call);
          }
          return;
        }

        const usage = normalizeRealtimeUsage(msg.response?.usage);
        const text = responseId ? (this.assistantTextByResponse.get(responseId) ?? '') : '';
        const timedOut = responseId ? this.timedOutResponseIds.has(responseId) : false;
        const interrupted = responseId ? this.interruptedResponseIds.has(responseId) : false;
        const repetitionStopped = responseId
          ? this.repetitionStoppedResponseIds.has(responseId)
          : false;
        const wasRecovery = responseId ? this.recoveryResponseIds.has(responseId) : false;
        const recoveryGeneration = responseId
          ? this.repetitionRecoveryGenerations.get(responseId)
          : undefined;
        const responseOutputItemId = responseId
          ? this.responseOutputItemIds.get(responseId)
          : undefined;
        const statusErrorCode =
          msg.response?.status_details?.error?.code ??
          msg.response?.status_details?.reason ??
          status;
        const turn: TranscriptTurnInput = {
          role: 'assistant',
          text,
          timestamp: new Date().toISOString(),
          modelUsed: this.descriptor.model,
          voiceUsed: voiceTelemetryValue(this.descriptor.voice),
          voiceProvider: 'openai-realtime',
          ...(latencyMs !== undefined ? { latencyMs } : {}),
          ...(usage ? { usage } : {}),
          // Interruption/timeout events are recorded separately so aggregate
          // counts are not doubled by both a turn flag and an event row.
          ...(!responseCompleted &&
          !timedOut &&
          !interrupted &&
          !repetitionStopped &&
          statusErrorCode
            ? { errorCode: statusErrorCode.slice(0, 80) }
            : {}),
        };
        if (turn.text.length > 0 || turn.usage) {
          this.recorder?.recordTurn(turn);
        }
        if (responseId) {
          this.assistantTextByResponse.delete(responseId);
          this.assistantTranscriptDeltasByResponse.delete(responseId);
          this.responseOutputItemIds.delete(responseId);
          this.interruptedResponseIds.delete(responseId);
          this.timedOutResponseIds.delete(responseId);
          this.repetitionStoppedResponseIds.delete(responseId);
          this.repetitionRecoveryGenerations.delete(responseId);
          this.recoveryResponseIds.delete(responseId);
        }
        if (repetitionStopped && responseOutputItemId) {
          this.safeSend(buildConversationItemDeletePayload(responseOutputItemId));
        }
        if (
          repetitionStopped &&
          !wasRecovery &&
          recoveryGeneration === this.inputGeneration &&
          !this.stopped
        ) {
          this.nextResponseIsRecovery = true;
          this.safeSend(
            buildRepetitionRecoveryResponseCreatePayload(
              this.descriptor.runtimePolicy.maxOutputTokens,
            ),
          );
        }
        return;
      }
      case 'error': {
        // Non-fatal by default — surface to caller, don't tear down the session.
        const message = msg.error?.message ?? 'OpenAI error';
        this.failPreparation(new Error(message));
        this.onError?.(new Error(message));
        this.recorder?.recordImmediateEvent({
          type: 'turn-error',
          timestamp: new Date().toISOString(),
          payload: {
            message,
            code: msg.error?.code ?? 'unknown',
          },
        });
        if (this.openingGreetingProtectionActive) {
          this.releaseOpeningGreetingProtection();
        }
        return;
      }
      case 'response.output_audio.delta': {
        if (typeof msg.delta === 'string' && msg.delta.length > 0) {
          if (!msg.response_id || !this.isCancelledResponse(msg.response_id)) {
            this.schedulePlayback(msg.delta, msg);
          }
        }
        return;
      }
      case 'response.output_audio_transcript.delta': {
        const responseId = msg.response_id ?? this.latestActiveResponseId();
        if (
          responseId &&
          typeof msg.delta === 'string' &&
          msg.delta.length > 0 &&
          !this.isCancelledResponse(responseId)
        ) {
          if (typeof msg.item_id === 'string') {
            this.responseOutputItemIds.set(responseId, msg.item_id);
          }
          const prior = this.assistantTranscriptDeltasByResponse.get(responseId) ?? '';
          const transcript = `${prior}${msg.delta}`;
          this.assistantTranscriptDeltasByResponse.set(responseId, transcript);
          if (hasRunawayResponseRepetition(transcript)) {
            this.repetitionRecoveryGenerations.set(responseId, this.inputGeneration);
            this.cancelResponse(responseId, 'response-repetition');
          }
        }
        return;
      }
      case 'response.output_audio_transcript.done': {
        const text = msg.transcript ?? msg.text;
        if (typeof text === 'string' && text.length > 0) {
          const responseId = msg.response_id ?? this.latestActiveResponseId();
          if (responseId) {
            const prior = this.assistantTextByResponse.get(responseId);
            this.assistantTextByResponse.set(responseId, prior ? `${prior} ${text}` : text);
          }
          this.onTranscript?.(text);
        }
        return;
      }
      case 'input_audio_buffer.speech_started': {
        if (this.openingGreetingProtectionActive) return;
        if (
          this.descriptor.runtimePolicy.interruptionMode === 'finish-response' &&
          (this.activeResponseIds.size > 0 || this.playbackSources.size > 0)
        ) {
          return;
        }
        this.inputGeneration += 1;
        this.supersedePendingFunctionCalls();
        const responseIds = new Set([
          ...this.activeResponseIds,
          ...[...this.playbackSources.values()].filter(
            (responseId): responseId is string => typeof responseId === 'string',
          ),
        ]);
        for (const responseId of responseIds) {
          if (this.activeResponseIds.has(responseId)) {
            this.cancelResponse(responseId, 'user-interruption');
          } else {
            this.interruptCompletedPlayback(responseId);
          }
        }
        // Older provider events may omit response_id. Playback must still
        // yield immediately even when context truncation is unavailable.
        if (this.playbackSources.size > 0) this.stopPlayback();
        return;
      }
      case 'conversation.item.input_audio_transcription.completed': {
        const text = (msg.transcript ?? msg.text)?.trim();
        if (this.openingGreetingProtectionActive) {
          if (msg.item_id) this.safeSend(buildConversationItemDeletePayload(msg.item_id));
          return;
        }
        if (
          this.descriptor.runtimePolicy.interruptionMode === 'finish-response' &&
          (this.activeResponseIds.size > 0 || this.playbackSources.size > 0)
        ) {
          if (msg.item_id) this.safeSend(buildConversationItemDeletePayload(msg.item_id));
          return;
        }
        if (typeof text === 'string' && isActionableTranscript(text)) {
          this.recorder?.recordTurn({
            role: 'user',
            text,
            timestamp: new Date().toISOString(),
            voiceProvider: 'openai-realtime',
          });
          this.onTranscript?.(text);
          this.safeSend(
            buildUserTurnResponseCreatePayload(this.descriptor.runtimePolicy.maxOutputTokens),
          );
        } else if (msg.item_id) {
          // Remove rejected noise from model context as well as suppressing
          // the automatic answer.
          this.safeSend(buildConversationItemDeletePayload(msg.item_id));
        }
        return;
      }
      default:
        return;
    }
  }

  private extractFunctionCalls(msg: RealtimeInboundEvent): RealtimeFunctionCall[] {
    return (
      msg.response?.output
        ?.filter((item) => item.type === 'function_call')
        .flatMap((item) => {
          if (
            typeof item.name !== 'string' ||
            typeof item.call_id !== 'string' ||
            typeof item.arguments !== 'string'
          ) {
            return [];
          }
          return [
            {
              name: item.name,
              callId: item.call_id,
              argumentsJson: item.arguments,
            },
          ];
        }) ?? []
    );
  }

  private async handleFunctionCall(call: RealtimeFunctionCall): Promise<void> {
    if (call.name !== KNOWLEDGE_SEARCH_TOOL_NAME) return;
    if (this.handledFunctionCalls.has(call.callId)) return;
    this.handledFunctionCalls.add(call.callId);
    this.pendingFunctionCalls.add(call.callId);
    const inputGeneration = this.inputGeneration;

    const startedAt = performance.now();
    const parsed = parseKnowledgeToolArguments(call.argumentsJson);
    let output: unknown;
    let resultCount = 0;
    let toolError: string | undefined;
    let sources: KnowledgeSourceSummary[] = [];

    try {
      if (!parsed) {
        toolError = 'Invalid search query.';
        output = { results: [], error: toolError };
      } else if (!this.options.knowledgeTool) {
        toolError = 'Knowledge search is not available.';
        output = { results: [], error: toolError };
      } else {
        const response = await this.options.knowledgeTool.search(parsed.query);
        resultCount = response.results.length;
        sources = summarizeKnowledgeSources(response.results);
        output = {
          results: response.results.map((result) => ({
            title: result.title,
            text: result.text,
            sourceId: result.sourceId,
          })),
        };
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Knowledge search failed.';
      toolError = message;
      output = { results: [], error: message };
      if (inputGeneration === this.inputGeneration) this.onError?.(new Error(message));
    }

    const superseded = this.supersededFunctionCalls.has(call.callId);
    if (superseded) {
      resultCount = 0;
      sources = [];
      toolError = 'Superseded by new user input.';
    }
    const durationMs = Math.max(0, Math.round(performance.now() - startedAt));
    const usage = normalizeRealtimeUsage(call.usage);
    const toolCall = {
      name: call.name,
      arguments: parsed ? { query: parsed.query } : {},
      result: {
        resultCount,
        sources,
        ...(toolError ? { error: toolError } : {}),
      },
      durationMs,
    };
    if (usage || parsed) {
      this.recorder?.recordTurn({
        role: 'assistant',
        text: '',
        timestamp: new Date().toISOString(),
        modelUsed: this.descriptor.model,
        voiceUsed: voiceTelemetryValue(this.descriptor.voice),
        voiceProvider: 'openai-realtime',
        ...(call.latencyMs !== undefined ? { latencyMs: call.latencyMs } : {}),
        ...(usage ? { usage } : {}),
        toolCalls: [toolCall],
      });
    }

    this.recorder?.recordEvent({
      type: 'tool-call',
      timestamp: new Date().toISOString(),
      payload: {
        name: call.name,
        ...(parsed ? { query: parsed.query } : {}),
        resultCount,
        durationMs,
        sources,
        ...(toolError ? { error: toolError } : {}),
      },
    });

    this.pendingFunctionCalls.delete(call.callId);
    this.supersededFunctionCalls.delete(call.callId);
    if (!superseded) this.safeSend(buildFunctionCallOutputPayload(call.callId, output));
    // A new user utterance may have arrived while RAG was in flight. Complete
    // the old function item, but never speak its now-stale answer over the new
    // turn.
    if (!superseded && inputGeneration === this.inputGeneration) {
      this.safeSend(
        buildToolFreeResponseCreatePayload(this.descriptor.runtimePolicy.maxOutputTokens),
      );
    }
  }

  private supersedePendingFunctionCalls(): void {
    for (const callId of this.pendingFunctionCalls) {
      this.pendingFunctionCalls.delete(callId);
      this.supersededFunctionCalls.add(callId);
      this.safeSend(
        buildFunctionCallOutputPayload(callId, {
          results: [],
          error: 'Superseded by new user input.',
        }),
      );
    }
  }

  private sendSessionUpdate(): void {
    // Matches the OpenAI Realtime GA schema (see web_clone PoC reference):
    // `session.instructions` at the top, voice nested under `audio.output`.
    //
    // `audio.input.transcription` enables Whisper to transcribe the user's
    // mic audio and emit `conversation.item.input_audio_transcription.completed`
    // events. Without this, only assistant turns appear in the transcript —
    // the dashboard would show one-sided conversations. Pass the active
    // ISO-639-1 runtime language to avoid Whisper auto-detecting the wrong
    // language on short utterances.
    this.safeSend(
      buildRealtimeSessionUpdatePayload({
        instructions: appendActiveLanguageInstruction(
          this.descriptor.instructions,
          this.activeInputLanguage,
          this.descriptor.runtimePolicy,
        ),
        voice: this.descriptor.voice,
        inputLanguage:
          this.descriptor.runtimePolicy.languageSwitchPolicy === 'automatic'
            ? undefined
            : this.activeInputLanguage,
        knowledgeToolEnabled:
          this.descriptor.knowledgePolicy.enabled && this.options.knowledgeTool !== undefined,
        knowledgeLookupPolicy: this.descriptor.knowledgePolicy.lookupPolicy,
        maxOutputTokens: this.descriptor.runtimePolicy.maxOutputTokens,
        turnTakingProfile: this.descriptor.runtimePolicy.turnTakingProfile,
      }),
    );
  }

  private safeSend(payload: unknown): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(payload));
    } catch (err) {
      if (this.stopped) return;
      this.onError?.(err instanceof Error ? err : new Error('WebSocket send failed'));
    }
  }

  private async startMicrophone(): Promise<void> {
    const audioCtx = this.audioCtx;
    if (!audioCtx || this.stopped) return;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: 24000,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });
    if (this.stopped) {
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    this.mediaStream = stream;

    const source = audioCtx.createMediaStreamSource(stream);
    this.sourceNode = source;

    const worklet = new AudioWorkletNode(audioCtx, 'pcm16-recorder');
    this.workletNode = worklet;

    worklet.port.onmessage = (e: MessageEvent<ArrayBuffer | Int16Array>): void => {
      const ws = this.ws;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (this.openingGreetingProtectionActive) return;
      if (
        this.descriptor.runtimePolicy.interruptionMode === 'finish-response' &&
        (this.activeResponseIds.size > 0 || this.playbackSources.size > 0)
      ) {
        return;
      }
      // The worklet posts the Int16Array's underlying buffer as transferable,
      // so `e.data` arrives as ArrayBuffer in Chromium. Handle both shapes.
      const pcm = e.data instanceof Int16Array ? e.data : new Int16Array(e.data as ArrayBuffer);
      const b64 = pcm16ToBase64(pcm);
      this.safeSend({ type: 'input_audio_buffer.append', audio: b64 });
    };

    source.connect(worklet);
    // Connect worklet to destination so the AudioWorkletProcessor is kept
    // running. The processor doesn't emit any output audio, so this has no
    // audible effect — it's required by the Web Audio graph.
    worklet.connect(audioCtx.destination);
  }

  private schedulePlayback(b64: string, event: RealtimeInboundEvent): void {
    const audioCtx = this.audioCtx;
    if (!audioCtx) return;

    const pcm = base64ToPcm16(b64);
    if (pcm.length === 0) return;
    const float32 = pcm16ToFloat32(pcm);

    const audioBuffer = audioCtx.createBuffer(1, float32.length, 24000);
    // lib.dom's copyToChannel signature is `Float32Array<ArrayBuffer>`, but
    // our helpers return `Float32Array<ArrayBufferLike>` (TS 5.7+ generic
    // typed arrays). The runtime behaviour is identical; the parameter type
    // is a lib.dom quirk, not a real constraint. Cast via `unknown` to keep
    // strict-mode happy.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (audioBuffer.copyToChannel as any)(float32, 0);

    const startTime = Math.max(audioCtx.currentTime, this.nextPlayTime);

    const source = audioCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioCtx.destination);
    source.onended = (): void => {
      this.playbackSources.delete(source);
      if (
        responseId &&
        ![...this.playbackSources.values()].some(
          (sourceResponseId) => sourceResponseId === responseId,
        )
      ) {
        this.playbackContexts.delete(responseId);
      }
      this.agentSpeechChunks = this.playbackSources.size;
      if (this.agentSpeechChunks === 0) {
        this.onAgentSpeechEnd?.();
      }
      if (responseId === this.openingGreetingResponseId) {
        this.maybeReleaseOpeningGreetingProtection();
      }
    };
    source.start(startTime);
    if (!this.firstAudioScheduled) {
      this.firstAudioScheduled = true;
      this.options.onFirstAudio?.();
    }
    this.nextPlayTime = startTime + audioBuffer.duration;

    const responseId = event.response_id;
    this.playbackSources.set(source, responseId);
    this.agentSpeechChunks = this.playbackSources.size;
    if (
      responseId &&
      typeof event.item_id === 'string' &&
      typeof event.content_index === 'number'
    ) {
      const prior = this.playbackContexts.get(responseId);
      this.playbackContexts.set(responseId, {
        responseId,
        itemId: event.item_id,
        contentIndex: event.content_index,
        startedAt: prior?.itemId === event.item_id ? prior.startedAt : startTime,
        scheduledUntil: startTime + audioBuffer.duration,
      });
    }
    if (this.agentSpeechChunks === 1) {
      this.onAgentSpeechStart?.();
    }
  }

  private latestActiveResponseId(): string | undefined {
    return [...this.activeResponseIds].at(-1);
  }

  private isCancelledResponse(responseId: string): boolean {
    return (
      this.interruptedResponseIds.has(responseId) ||
      this.timedOutResponseIds.has(responseId) ||
      this.repetitionStoppedResponseIds.has(responseId)
    );
  }

  private startResponseTimeout(responseId: string): void {
    this.clearResponseTimeout(responseId);
    const timer = setTimeout(
      () => {
        if (!this.activeResponseIds.has(responseId) || this.stopped) return;
        this.cancelResponse(responseId, 'response-timeout');
      },
      Math.min(
        this.descriptor.runtimePolicy.responseTimeoutMs,
        REALTIME_PLATFORM_MAX_RESPONSE_TIMEOUT_MS,
      ),
    );
    this.responseTimeouts.set(responseId, timer);
  }

  private clearResponseTimeout(responseId: string): void {
    const timer = this.responseTimeouts.get(responseId);
    if (timer) clearTimeout(timer);
    this.responseTimeouts.delete(responseId);
  }

  private maybeReleaseOpeningGreetingProtection(): void {
    const responseId = this.openingGreetingResponseId;
    if (
      !this.openingGreetingProtectionActive ||
      !this.openingGreetingResponseDone ||
      !responseId ||
      [...this.playbackSources.values()].some((sourceResponseId) => sourceResponseId === responseId)
    ) {
      return;
    }
    this.releaseOpeningGreetingProtection();
  }

  private releaseOpeningGreetingProtection(): void {
    this.openingGreetingProtectionActive = false;
    this.openingGreetingResponsePending = false;
    this.openingGreetingResponseId = undefined;
    this.openingGreetingResponseDone = false;
  }

  private cancelResponse(
    responseId: string,
    reason: 'user-interruption' | 'response-timeout' | 'response-repetition',
  ): void {
    if (!this.activeResponseIds.has(responseId)) return;
    this.activeResponseIds.delete(responseId);
    this.clearResponseTimeout(responseId);
    if (reason === 'user-interruption') this.interruptedResponseIds.add(responseId);
    else if (reason === 'response-timeout') this.timedOutResponseIds.add(responseId);
    else this.repetitionStoppedResponseIds.add(responseId);

    this.safeSend(buildResponseCancelPayload(responseId));
    const truncation = this.playbackTruncation(responseId);
    this.stopPlayback(responseId);
    if (responseId === this.openingGreetingResponseId) {
      this.releaseOpeningGreetingProtection();
    }
    if (truncation) this.safeSend(buildConversationItemTruncatePayload(truncation));

    this.recorder?.recordImmediateEvent({
      type: reason === 'user-interruption' ? 'interruption' : 'turn-error',
      timestamp: new Date().toISOString(),
      payload: {
        code: reason,
        responseId,
        ...(truncation ? { audioEndMs: truncation.audioEndMs } : {}),
      },
    });
  }

  private interruptCompletedPlayback(responseId: string): void {
    const truncation = this.playbackTruncation(responseId);
    this.stopPlayback(responseId);
    if (truncation) this.safeSend(buildConversationItemTruncatePayload(truncation));
    this.recorder?.recordImmediateEvent({
      type: 'interruption',
      timestamp: new Date().toISOString(),
      payload: {
        code: 'user-interruption',
        responseId,
        ...(truncation ? { audioEndMs: truncation.audioEndMs } : {}),
      },
    });
  }

  private playbackTruncation(
    responseId: string,
  ): { itemId: string; contentIndex: number; audioEndMs: number } | undefined {
    const audioCtx = this.audioCtx;
    const context = this.playbackContexts.get(responseId);
    if (!audioCtx || !context) return undefined;
    const playedSec = Math.max(
      0,
      Math.min(
        audioCtx.currentTime - context.startedAt,
        context.scheduledUntil - context.startedAt,
      ),
    );
    return {
      itemId: context.itemId,
      contentIndex: context.contentIndex,
      audioEndMs: Math.round(playedSec * 1000),
    };
  }

  private stopPlayback(responseId?: string): void {
    const wasSpeaking = this.playbackSources.size > 0;
    for (const [source, sourceResponseId] of [...this.playbackSources]) {
      if (responseId !== undefined && sourceResponseId !== responseId) continue;
      source.onended = null;
      try {
        source.stop();
      } catch {
        /* source may already have ended */
      }
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
      this.playbackSources.delete(source);
    }
    if (responseId) this.playbackContexts.delete(responseId);
    else this.playbackContexts.clear();
    this.agentSpeechChunks = this.playbackSources.size;
    const scheduledUntil = [...this.playbackContexts.values()].map(
      (context) => context.scheduledUntil,
    );
    this.nextPlayTime =
      this.playbackSources.size > 0 && scheduledUntil.length > 0
        ? Math.max(...scheduledUntil)
        : (this.audioCtx?.currentTime ?? 0);
    if (wasSpeaking && this.agentSpeechChunks === 0) this.onAgentSpeechEnd?.();
  }
}
